import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaExclamationTriangle, FaInfoCircle, FaTimes } from 'react-icons/fa';
import { formatPrice } from '../../utils/currency';

export const CANCELLATION_REASONS = [
  { value: 'changed-mind', label: 'Changed my mind' },
  { value: 'found-cheaper', label: 'Found a better price elsewhere' },
  { value: 'wrong-item', label: 'Ordered wrong item' },
  { value: 'delivery-delay', label: 'Delivery taking too long' },
  { value: 'no-longer-needed', label: 'No longer needed' },
  { value: 'payment-issue', label: 'Payment issue' },
  { value: 'other', label: 'Other reason' },
];

function resolveReasonValue(reasonKey, customReason) {
  if (!reasonKey) return '';
  if (reasonKey === 'other') return (customReason || '').trim();
  return CANCELLATION_REASONS.find((r) => r.value === reasonKey)?.label || reasonKey;
}

/**
 * B-08 — Custom cancel-order modal (replaces browser confirm on tracking page).
 */
export default function CancelOrderModal({
  isOpen,
  onClose,
  order,
  onConfirm,
  submitting = false,
  cancelFeeRequired = false,
}) {
  const [reasonKey, setReasonKey] = useState('');
  const [customReason, setCustomReason] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setReasonKey('');
      setCustomReason('');
    }
  }, [isOpen]);

  if (!isOpen || !order) return null;

  const isPaid = order.paymentStatus === 'Paid' || order.paymentStatus === 'paid';
  const reasonValid =
    reasonKey &&
    (reasonKey !== 'other' || (customReason.trim().length >= 10));

  const handleConfirm = () => {
    const reason = resolveReasonValue(reasonKey, customReason);
    if (!reason) return;
    onConfirm(reason, { payCancelFee: cancelFeeRequired });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10050] flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center"
        onClick={() => !submitting && onClose()}
        role="presentation"
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-600 bg-[#0f172a] shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-order-title"
        >
          <div className="flex items-start justify-between gap-3 border-b border-slate-700/80 px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-950/60 text-red-400">
                <FaExclamationTriangle />
              </span>
              <div>
                <h2 id="cancel-order-title" className="text-lg font-bold text-white">
                  Cancel order?
                </h2>
                <p className="mt-0.5 font-mono text-xs text-amber-400">{order.orderId}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-slate-200 disabled:opacity-40"
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
            <p className="text-sm leading-relaxed text-slate-300">
              {order.partName || 'This order'} will be cancelled. This action cannot be undone.
            </p>

            {cancelFeeRequired ? (
              <div className="mt-4 flex gap-3 rounded-xl border border-orange-700/50 bg-orange-950/30 px-4 py-3 text-sm text-orange-100">
                <FaInfoCircle className="mt-0.5 shrink-0 text-orange-400" />
                <p>
                  Seller already uploaded a dispatch photo. Cancelling now costs a{' '}
                  <strong>₹100 fee</strong>. You will receive{' '}
                  <strong>{formatPrice(Math.max(0, Number(order.total) - 100))}</strong> back.
                </p>
              </div>
            ) : (
              isPaid && (
                <div className="mt-4 flex gap-3 rounded-xl border border-amber-700/40 bg-amber-950/25 px-4 py-3 text-sm text-amber-100">
                  <FaInfoCircle className="mt-0.5 shrink-0 text-amber-400" />
                  <p>
                    Your payment of{' '}
                    <strong>{formatPrice(order.total)}</strong> will be refunded within{' '}
                    <strong>5–7 business days</strong>.
                  </p>
                </div>
              )
            )}

            <div className="mt-5">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                Reason for cancellation
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {CANCELLATION_REASONS.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setReasonKey(r.value)}
                    disabled={submitting}
                    className={`rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                      reasonKey === r.value
                        ? 'border-red-500/60 bg-red-950/40 text-red-200'
                        : 'border-slate-600 bg-slate-900/50 text-slate-300 hover:border-slate-500'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {reasonKey === 'other' && (
              <div className="mt-4">
                <label className="mb-1.5 block text-xs font-semibold text-slate-400">
                  Describe your reason (min 10 characters)
                </label>
                <textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  rows={3}
                  disabled={submitting}
                  placeholder="Tell us why you are cancelling…"
                  className="w-full rounded-xl border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-red-500/50 focus:outline-none focus:ring-1 focus:ring-red-500/30"
                />
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-slate-700/80 px-5 py-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-xl border border-slate-600 px-5 py-3 text-sm font-semibold text-slate-300 hover:bg-slate-800 disabled:opacity-40"
            >
              Keep order
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!reasonValid || submitting}
              className="rounded-xl bg-red-600 px-5 py-3 text-sm font-bold text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting
                ? 'Cancelling…'
                : cancelFeeRequired
                ? 'Pay ₹100 fee & cancel'
                : 'Confirm cancellation'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
